import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ReceiptText, Save, Hash } from 'lucide-react';

interface ReceiptSettingsRow {
  id: number;
  seller_name: string;
  seller_tin: string;
  seller_vat_reg: string | null;
  seller_address: string;
  seller_phone: string | null;
  seller_email: string | null;
  logo_url: string | null;
  vat_rate: number | string;
  receipt_prefix: string;
  footer_notes: string | null;
}

export default function ReceiptSettings() {
  const { hasRole, rolesLoading } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState<ReceiptSettingsRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [nextNum, setNextNum] = useState<string>('');
  const [resetting, setResetting] = useState(false);

  const allowed = hasRole('superadmin') || hasRole('accounting_manager' as any);

  const { data, isLoading } = useQuery({
    queryKey: ['receipt-settings'],
    enabled: allowed,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('receipt_settings' as any)
        .select('*')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as ReceiptSettingsRow;
    },
  });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const { data: lastReceipt } = useQuery({
    queryKey: ['receipts-latest-num'],
    enabled: allowed,
    queryFn: async () => {
      const { data } = await supabase
        .from('receipts' as any)
        .select('receipt_number, issued_at')
        .order('issued_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data as any;
    },
  });

  if (rolesLoading) return <Skeleton className="h-40" />;
  if (!allowed) return <Navigate to="/dashboard" replace />;

  const update = (k: keyof ReceiptSettingsRow, v: any) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  const save = async () => {
    if (!form) return;
    if (!form.seller_name?.trim() || !form.seller_tin?.trim() || !form.seller_address?.trim()) {
      toast.error('Business name, TIN and address are required.');
      return;
    }
    const rate = Number(form.vat_rate);
    if (Number.isNaN(rate) || rate < 0 || rate > 100) {
      toast.error('VAT rate must be between 0 and 100.');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('receipt_settings' as any)
        .update({
          seller_name: form.seller_name.trim(),
          seller_tin: form.seller_tin.trim(),
          seller_vat_reg: form.seller_vat_reg?.trim() || null,
          seller_address: form.seller_address.trim(),
          seller_phone: form.seller_phone?.trim() || null,
          seller_email: form.seller_email?.trim() || null,
          logo_url: form.logo_url?.trim() || null,
          vat_rate: rate,
          receipt_prefix: (form.receipt_prefix || 'RC').trim().toUpperCase(),
          footer_notes: form.footer_notes?.trim() || null,
        })
        .eq('id', 1);
      if (error) throw error;
      toast.success('Receipt settings saved. New receipts will use these values.');
      qc.invalidateQueries({ queryKey: ['receipt-settings'] });
    } catch (e: any) {
      toast.error(e.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetNumber = async () => {
    const n = parseInt(nextNum, 10);
    if (!Number.isFinite(n) || n < 1) {
      toast.error('Enter a positive whole number.');
      return;
    }
    if (!confirm(`Set the next receipt number to ${n}? Existing receipts are not affected.`)) return;
    setResetting(true);
    try {
      const { error } = await supabase.rpc('set_next_receipt_number' as any, { _next: n });
      if (error) throw error;
      toast.success(`Next receipt will be numbered ${form?.receipt_prefix || 'RC'}-YYYY-${String(n).padStart(6, '0')}.`);
      setNextNum('');
    } catch (e: any) {
      toast.error(e.message || 'Failed to update sequence');
    } finally {
      setResetting(false);
    }
  };

  if (isLoading || !form) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-6 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ReceiptText className="h-5 w-5 text-primary" /> Receipt Settings
          </CardTitle>
          <CardDescription>
            These details appear on every receipt &amp; invoice PDF issued to buyers. Changes apply to
            receipts created from now on; existing receipts keep the values that were saved at the time of issue.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Business / Legal Name *</Label>
              <Input value={form.seller_name} onChange={(e) => update('seller_name', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>TIN Number *</Label>
              <Input value={form.seller_tin} onChange={(e) => update('seller_tin', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>VAT Registration No.</Label>
              <Input value={form.seller_vat_reg ?? ''} onChange={(e) => update('seller_vat_reg', e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Address *</Label>
              <Input value={form.seller_address} onChange={(e) => update('seller_address', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.seller_phone ?? ''} onChange={(e) => update('seller_phone', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.seller_email ?? ''} onChange={(e) => update('seller_email', e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Logo URL (optional)</Label>
              <Input value={form.logo_url ?? ''} onChange={(e) => update('logo_url', e.target.value)} placeholder="https://…" />
            </div>
          </div>

          <Separator />

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>VAT Rate (%)</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={form.vat_rate as any}
                onChange={(e) => update('vat_rate', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Receipt Number Prefix</Label>
              <Input
                value={form.receipt_prefix}
                onChange={(e) => update('receipt_prefix', e.target.value.toUpperCase())}
                maxLength={8}
              />
              <p className="text-[11px] text-muted-foreground">
                Format: <code>{(form.receipt_prefix || 'RC')}-{new Date().getFullYear()}-000123</code>
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Footer Notes (optional)</Label>
            <Textarea
              rows={3}
              value={form.footer_notes ?? ''}
              onChange={(e) => update('footer_notes', e.target.value)}
              placeholder="e.g. Thank you for your business. Computer-generated receipt."
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving}>
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Hash className="h-5 w-5 text-primary" /> Initial / Next Receipt Number
          </CardTitle>
          <CardDescription>
            Last issued: <span className="font-mono">{lastReceipt?.receipt_number || '—'}</span>. Set
            the next sequential number used by the system. Use this once at fiscal-year start or when
            migrating from an external system. Existing receipts are not renumbered.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label>Next number</Label>
              <Input
                type="number"
                min={1}
                value={nextNum}
                onChange={(e) => setNextNum(e.target.value)}
                placeholder="e.g. 1000"
                className="w-48"
              />
            </div>
            <Button variant="outline" onClick={resetNumber} disabled={resetting || !nextNum}>
              {resetting ? 'Updating…' : 'Update Sequence'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
